import { useApolloClient } from "@apollo/client";
import { getDataOrNull, omit } from "@msys/common";
import {
  CardItem,
  DebouncedSearchInput,
  ListHeaderActiveFilters,
  ListHeaderActiveFiltersActionButton,
  ModalOpenButton,
  useFormatting,
  useScreenWidth,
} from "@msys/ui";
import {
  OpenInNew as OpenInNewIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  Stack,
  TablePagination,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { uniqBy } from "lodash-es";
import React from "react";
import { Link, useLocation } from "react-router-dom";
import {
  namedOperations,
  OrganisationProjectsSorting,
  ProjectStateMachineStatus,
} from "../../../clients/graphqlTypes.js";
import { RestrictedByOrganisationPermissionWithDebug } from "../../auth/RestrictedByOrganisationPermission.js";
import { RestrictedByProjectPermissionWithDebug } from "../../auth/RestrictedByProjectPermission.js";
import { useUserData } from "../../auth/useUserData.js";
import { FilterButton } from "../../commons/filters/FilterButton.js";
import { useFiltersAndPagination } from "../../commons/filters/useFiltersAndPagination.js";
import { useStateWithUrlParam } from "../../commons/hooks/useStateWithUrlParam.js";
import { Page, PageTopbarItem } from "../../commons/layout/Page.js";
import { PageContainer } from "../../commons/layout/PageContainer.js";
import {
  EditorView,
  EditorViewSwitch,
} from "../../features/planning/EditorViewSwitch.js";
import {
  PlanningMap,
  PlanningMapRef,
} from "../../features/planning/PlanningMap.js";
import { PlanningSchedule } from "../../features/planning/PlanningSchedule.js";
import { usePlanningMapProjectData } from "../../features/planning/usePlanningMapProjectData.js";
import { CreateProjectButton } from "../../features/projects/buttons/CreateProjectButton.js";
import { ProjectListItem } from "../../features/projects/components/ProjectListItem.js";
import {
  FilterProjectPlanningButton,
  ProjectFilterPlanning,
} from "../../features/projects/filters/ProjectPlanning.js";
import { ProjectStatesFilterButton } from "../../features/projects/filters/ProjectState.js";
import { ProjectsFilterModal } from "../../features/projects/ProjectsFilterModal.js";
import { getProjectFilterChipsFactory } from "../../features/projects/renderProjectsFilterChips.js";
import {
  PROJECT_FILTER_MAP,
  ProjectSourceFilterType,
} from "../../features/projects/useProjectSources.js";
import { CalendarViewLayout } from "../../features/schedule/CalendarViewLayout.js";
import { SchedulePlanSessionFragment } from "../../features/schedule/Fragments.generated.js";
import { useListViewCustomQuickFilters } from "../../features/users/useListViewCustomQuickFilters.js";
import {
  PlanningProjectsQueryVariables,
  usePlanningProjects_OrganisationQuery,
  usePlanningProjectsQuery,
} from "./PlanningProjects.generated.js";
import { useSelectedDate } from "./useSelectedDate.js";

const DEFAULT_ITEMS_PER_PAGE = 25;
const DEFAULT_SORTING: OrganisationProjectsSorting[] = [
  {
    column: "createdAt",
    direction: "desc",
  },
];

type Filters = Omit<
  PlanningProjectsQueryVariables,
  "offset" | "limit" | "sorting" | "filterBySources"
> & {
  state?: ProjectStateMachineStatus[];
  planning?: ProjectFilterPlanning[];
  sources?: ProjectSourceFilterType[] | undefined | null;
};

interface Props {
  submenuItems: PageTopbarItem[];
}

export const PlanningProjects = ({ submenuItems }: Props) => {
  const viewer = useUserData().currentUser!;
  const { t } = useTranslate([
    "Global",
    "SidebarItems",
    "Projects",
    "Tickets",
    "Opportunities",
  ]);
  const { isMaxPhone, isMinTablet } = useScreenWidth();
  const location = useLocation();

  const [editorView, setEditorView] = React.useState<EditorView>(
    EditorView.List
  );

  const [projectIdsForMapFromSchedule, setProjectIdsForMapFromSchedule] =
    React.useState<string[]>([]);

  const projectsForMapFromSchedule = usePlanningMapProjectData(
    projectIdsForMapFromSchedule
  );

  const { getFormattedPrice, getFormattedFloat } = useFormatting();

  const allowedStates: ProjectStateMachineStatus[] = [
    "contracted",
    "opportunity",
  ];

  const {
    offset,
    limit,
    paginationModel,
    setPaginationModel,
    filters,
    setFilters,
    resetFilters,
    sorting,
    setSorting,
    toRemoveParams,
  } = useFiltersAndPagination<OrganisationProjectsSorting, Filters>(
    {},
    DEFAULT_SORTING,
    DEFAULT_ITEMS_PER_PAGE
  );

  const listViewName = "PlanningProjects";

  const { customQuickFilterSelect, addCustomFilterButton } =
    useListViewCustomQuickFilters<Filters>(listViewName, filters, setFilters, [
      {
        filterName: t("Relevant to me", { ns: "Opportunities" }),
        filterValue: {
          filterRelevantToUserIds: [viewer.id],
        },
      },
    ]);

  const searchRef = React.useRef<HTMLInputElement>(null);
  const [searchVisible, setSearchVisible] = React.useState<boolean>(
    Boolean(filters?.filterSearchTerm)
  );

  const toggleSearchVisible = () => {
    if (!searchVisible) {
      setSearchVisible(true);
      setTimeout(() => {
        searchRef.current?.focus();
      });
    } else {
      setSearchVisible(false);
    }
  };

  const client = useApolloClient();

  const projectsQuery = usePlanningProjectsQuery({
    client,
    fetchPolicy: "cache-and-network",
    variables: {
      limit,
      offset,
      ...filters,
      filterBySources: filters.sources
        ? filters.sources.map(s => PROJECT_FILTER_MAP[s]).flat(1)
        : undefined,
      filterIncludeState: filters.filterIncludeState?.length
        ? filters.filterIncludeState
        : allowedStates,
      sorting,
    },
  });

  const organisationQuery = usePlanningProjects_OrganisationQuery({
    client,
    fetchPolicy: "cache-and-network",
  });

  const projects = React.useMemo(
    () =>
      getDataOrNull(
        (projectsQuery.data ?? projectsQuery.previousData)?.projects
      )?.edges.map(e => e.node) ?? [],
    [projectsQuery.data, projectsQuery.previousData]
  );

  const totalCount =
    getDataOrNull((projectsQuery.data ?? projectsQuery.previousData)?.projects)
      ?.totalCount ?? 0;

  const roles = React.useMemo(
    () => organisationQuery.data?.roles ?? [],
    [organisationQuery.data?.roles]
  );

  const projectsForMap = React.useMemo(
    () => uniqBy([...projects, ...projectsForMapFromSchedule], p => p.id),
    [projects, projectsForMapFromSchedule]
  );

  const mapRef = React.useRef<PlanningMapRef>(null);

  const [selectedProjectId, setSelectedProjectId] = useStateWithUrlParam<
    string | null
  >("selectedId", null);

  const selectedProject = React.useMemo(
    () => projects.find(item => item.id === selectedProjectId),
    [projects, selectedProjectId]
  );

  React.useEffect(() => {
    const project = projects?.find(p => p.id === selectedProjectId);
    if (project && project.buildingInfo?.id)
      mapRef.current?.setSelectedBuildingId(project.buildingInfo.id);
  }, [projects, selectedProjectId]);

  React.useEffect(() => {
    if (projectsQuery.loading || projectsQuery.error) return;
    if (selectedProjectId && !selectedProject) {
      setSelectedProjectId(null);
    }
  }, [
    projectsQuery.loading,
    projectsQuery.error,
    selectedProject,
    selectedProjectId,
    setSelectedProjectId,
  ]);

  const selectedDates = useSelectedDate(selectedProject);

  const handlePlanSessionClick = React.useCallback(
    (planSession: SchedulePlanSessionFragment) => {
      mapRef.current?.setSelectedBuildingId(
        planSession.project.buildingInfo?.id
      );
    },
    []
  );

  const getActiveProjectFilterChips = getProjectFilterChipsFactory({
    viewer,
    t,
    allowedStates,
    getFormattedFloat,
    showPhasesChip: true,
  });
  const activeFilterChips = getActiveProjectFilterChips({
    filters,
    setFilters,
  });

  const List = (
    <Stack
      direction="column"
      spacing={1}
      sx={{
        flex: 1,
        width: "100%",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <Stack
        direction="column"
        spacing={1}
        sx={{
          flexGrow: 0,
          flexShrink: 0,
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          sx={{
            width: "100%",
            justifyContent: "space-between",
            alignItems: "center",
            minWidth: 0,
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: "center",
              flexShrink: 1,
              flexGrow: 1,
            }}
          >
            <ProjectStatesFilterButton
              value={filters.filterIncludeState ?? []}
              setValue={newValue => {
                return newValue.length > 0
                  ? setFilters(state => ({
                      ...state,
                      filterIncludeState: newValue,
                    }))
                  : setFilters(state => omit(state, "filterIncludeState"));
              }}
              allowedStates={allowedStates}
            />
            <FilterProjectPlanningButton
              value={filters.filterPlanningState ?? []}
              setValue={newValue =>
                newValue.length > 0
                  ? setFilters(state => ({
                      ...state,
                      filterPlanningState: newValue,
                    }))
                  : setFilters(state => omit(state, "filterPlanningState"))
              }
            />
            {customQuickFilterSelect}
            <ModalOpenButton
              Modal={ProjectsFilterModal}
              modalProps={{
                sorting,
                setSorting,
                filters,
                setFilters,
                resetFilters,
                allowedStates,
                showPhasesFilter: true,
                showSorting: true,
              }}
            >
              <FilterButton />
            </ModalOpenButton>

            <IconButton
              style={{
                flexShrink: 0,
                flexGrow: 0,
                width: 30,
                height: 30,
              }}
              size="small"
              color="primary"
              onClick={toggleSearchVisible}
            >
              <SearchIcon />
            </IconButton>
          </Stack>

          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: "center",
            }}
          >
            {isMaxPhone && (
              <EditorViewSwitch value={editorView} setValue={setEditorView} />
            )}
            <RestrictedByOrganisationPermissionWithDebug permission="CREATE_PROJECT">
              <CreateProjectButton
                type="button"
                pageName="PlanningProjects"
                refetchQueries={[namedOperations.Query.PlanningProjects]}
              />
            </RestrictedByOrganisationPermissionWithDebug>
          </Stack>
        </Stack>
        {searchVisible && (
          <Box>
            <DebouncedSearchInput
              inputRef={searchRef}
              fullWidth={isMaxPhone}
              placeholder={t("Search", { ns: "Global" })}
              defaultValue={filters?.filterSearchTerm ?? ""}
              onChangeSearchTerm={newValue =>
                setFilters(filterVariables => ({
                  ...filterVariables,
                  filterSearchTerm: newValue,
                }))
              }
            />
          </Box>
        )}
        <ListHeaderActiveFilters
          activeFilterChips={activeFilterChips}
          collapsedContent={
            <Typography variant="body2">
              {t("Active filters", { ns: "Global" })}
              <Typography
                variant="body2"
                component="span"
                sx={{
                  marginLeft: 1,
                  fontWeight: "bold",
                }}
              >
                {t("{count} results", {
                  ns: "Global",
                  count: activeFilterChips.length,
                })}
              </Typography>
            </Typography>
          }
          clearAllButton={
            <ListHeaderActiveFiltersActionButton
              label={t("Clear all", { ns: "Global" })}
              onClick={resetFilters}
            />
          }
          saveFiltersButton={addCustomFilterButton}
        />
      </Stack>

      <Stack
        direction="column"
        spacing={0.5}
        sx={{
          flexGrow: 1,
          flexShrink: 1,
          overflow: "auto",
        }}
      >
        {projects.map(project => (
          <CardItem
            key={project.id}
            onClick={() => {
              setSelectedProjectId(project.id);
            }}
            selected={project === selectedProject}
          >
            <ProjectListItem
              project={project}
              Action={
                <Stack
                  direction="row"
                  spacing={0.5}
                  sx={{
                    alignItems: "center",
                  }}
                >
                  <IconButton
                    size="small"
                    color="primary"
                    style={{ flexGrow: 0, flexShrink: 0 }}
                    onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                      e.stopPropagation();
                    }}
                    component={Link}
                    to={`/projects/${project.id}`}
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    <Tooltip
                      title={t("Open project", {
                        ns: "Projects",
                      })}
                      placement="top"
                      arrow
                    >
                      <OpenInNewIcon fontSize="small" />
                    </Tooltip>
                  </IconButton>

                  {project.plannedUsers.length > 0 && (
                    <RestrictedByProjectPermissionWithDebug
                      permission="MANAGE_PROJECT"
                      project={project}
                    >
                      {viewer.organisation.id ===
                        project.owningSystemOrganisationId && (
                        <Button
                          size="extra-small"
                          color="primary"
                          variant="outlined"
                          onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                            e.stopPropagation();
                          }}
                          component={Link}
                          to={{
                            pathname: `/planning/projects/${project.id}`,
                            search: new URLSearchParams({
                              r: location.pathname + location.search,
                            }).toString(),
                          }}
                        >
                          {t("Re-schedule", { ns: "Projects" })}
                        </Button>
                      )}
                    </RestrictedByProjectPermissionWithDebug>
                  )}

                  {project.plannedUsers.length === 0 && (
                    <RestrictedByProjectPermissionWithDebug
                      permission="MANAGE_PROJECT"
                      project={project}
                    >
                      {viewer.organisation.id ===
                        project.owningSystemOrganisationId && (
                        <Button
                          size="extra-small"
                          color="primary"
                          variant="outlined"
                          onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                            e.stopPropagation();
                          }}
                          component={Link}
                          to={{
                            pathname: `/planning/projects/${project.id}`,
                            search: new URLSearchParams({
                              r: location.pathname + location.search,
                            }).toString(),
                          }}
                        >
                          {t("Schedule", { ns: "Projects" })}
                        </Button>
                      )}
                    </RestrictedByProjectPermissionWithDebug>
                  )}
                </Stack>
              }
            />
          </CardItem>
        ))}
      </Stack>

      <TablePagination
        component="div"
        count={totalCount}
        page={paginationModel.page}
        onPageChange={(event, newPage) => {
          setPaginationModel({
            pageSize: paginationModel.pageSize,
            page: newPage,
          });
        }}
        rowsPerPage={paginationModel.pageSize}
        onRowsPerPageChange={event => {
          setPaginationModel({
            pageSize: parseInt(event.target.value, 10),
            page: 0,
          });
        }}
        labelRowsPerPage={t("Per page:", { ns: "Global" })}
      />
    </Stack>
  );

  const [resourceMaxDistance, setResourceMaxDistance] =
    React.useState<number>(0);

  const Map = (
    <PlanningMap
      ref={mapRef}
      projects={projectsForMap}
      selectedProjectAddress={
        selectedProject?.buildingInfo?.buildingAddress ?? undefined
      }
      resourceMaxDistance={resourceMaxDistance}
      maxWidth={9999}
      RightButtons={
        isMaxPhone ? (
          <>
            <EditorViewSwitch value={editorView} setValue={setEditorView} />
            <RestrictedByOrganisationPermissionWithDebug permission="CREATE_PROJECT">
              <CreateProjectButton
                type="button"
                pageName="PlanningProjects"
                refetchQueries={[
                  namedOperations.Query.ProjectsList,
                  namedOperations.Query.PlanningProjects,
                ]}
              />
            </RestrictedByOrganisationPermissionWithDebug>
          </>
        ) : undefined
      }
    />
  );

  const Calendar = (
    <PlanningSchedule
      key={`planning-schedule-${selectedProject?.id ?? "all"}`}
      usePinnedResources={false}
      roles={roles}
      view={"TimelineWeek"}
      selectedProjectId={selectedProject?.id}
      projectAddress={
        selectedProject?.buildingInfo?.buildingAddress ?? undefined
      }
      useMaxDistance={Boolean(
        selectedProject?.id && selectedProject?.buildingInfo?.buildingAddress
      )}
      activeDates={selectedDates}
      onPlanSessionClick={handlePlanSessionClick}
      onResourceMaxDistanceChange={setResourceMaxDistance}
      onProjectsChange={setProjectIdsForMapFromSchedule}
      RightButtons={
        isMaxPhone ? (
          <>
            <EditorViewSwitch value={editorView} setValue={setEditorView} />
            <RestrictedByOrganisationPermissionWithDebug permission="CREATE_PROJECT">
              <CreateProjectButton
                type="button"
                pageName="PlanningProjects"
                refetchQueries={[
                  namedOperations.Query.ProjectsList,
                  namedOperations.Query.PlanningProjects,
                ]}
              />
            </RestrictedByOrganisationPermissionWithDebug>
          </>
        ) : undefined
      }
    />
  );

  return (
    <Page
      subtitle={t("Planning and Resources", {
        ns: "SidebarItems",
      })}
      title={t("Projects", {
        ns: "Projects",
      })}
      submenuItems={submenuItems}
    >
      <PageContainer style={{ minHeight: "100%", height: "unset" }}>
        {isMinTablet ? (
          <CalendarViewLayout
            name="projects"
            List={
              <Stack
                spacing={1}
                direction="row"
                sx={{
                  height: "100%",
                  flexGrow: 1,
                  flexShrink: 1,
                  justifyContent: "space-between",
                  alignItems: "stretch",
                }}
              >
                <Box
                  sx={{
                    height: "100%",
                    flexShrink: 0,
                    flexGrow: 0,
                    overflow: "auto",
                    width: { xs: "620px", lg: "720px" },
                  }}
                >
                  {List}
                </Box>
                <Box
                  sx={{
                    height: "100%",
                    flexGrow: 1,
                    flexShrink: 1,
                  }}
                >
                  {Map}
                </Box>
              </Stack>
            }
            Calendar={Calendar}
          />
        ) : (
          <Stack
            spacing={1}
            direction="column"
            sx={{
              flex: 1,
              justifyContent: "stretch",
              alignItems: "stretch",
            }}
          >
            {editorView === EditorView.List && List}
            {editorView === EditorView.Map && Map}
            {editorView === EditorView.Calendar && Calendar}
          </Stack>
        )}
      </PageContainer>
    </Page>
  );
};
